import { useState, useEffect, useCallback, useMemo, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Globe, Zap, Shield, Headphones, ChevronRight, X, ShoppingCart, Menu, ChevronDown, Scale, Map, Calculator, MapPin, Phone, Mail, UserCircle, Heart, Lock, CreditCard, Languages, Route } from 'lucide-react';
import axios from 'axios';
import { CompareButton } from './Compare';
import { getSupportEmail, openMailTo, openWhatsApp, getWhatsAppNumber } from '../utils/obfuscate';
import { InteractiveGlobe, CountryPlansPopup, COUNTRIES_DATA } from './InteractiveGlobe';
import { useAuth } from '../context/AuthContext';
import { useWishlist } from '../context/WishlistContext';
import { searchProducts } from '../utils/countryNames';
import { useCurrency } from '../context/CurrencyContext';
import { getLandmarkUrl } from '../utils/fallbackImages';

const Globe3D = lazy(() => import('./Globe3D'));

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' }
];

export function Navbar({ cartCount, onCartClick, onNavigate, currentPage }) {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isCurrOpen, setIsCurrOpen] = useState(false);
  const { user } = useAuth();
  const { currency, setCurrency, currencyConfig, allCurrencies } = useCurrency();

  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const navItems = [
    { key: 'home', label: t('nav.home'), page: 'home' },
    { key: 'shop', label: t('nav.shop'), page: 'shop' },
    { key: 'map', label: t('nav.map'), page: 'map', icon: Map },
    { key: 'calculator', label: t('nav.calculator'), page: 'calculator', icon: Calculator },
    { key: 'compatibility', label: t('nav.compatibility'), page: 'compatibility', icon: Phone },
    { key: 'about', label: t('nav.about'), page: 'about' },
    { key: 'howItWorks', label: t('nav.howItWorks'), page: 'how-it-works' },
    { key: 'faq', label: t('nav.faq'), page: 'faq' },
    { key: 'contact', label: t('nav.contact'), page: 'contact' }
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0f1419]/80 backdrop-blur-xl border-b border-white/10 overflow-visible" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <button 
            onClick={() => onNavigate('home')} 
            className="flex items-center self-start pt-1"
            data-testid="logo-button"
          >
            <img src="/esim4you_logo.svg" alt="eSIM4You" className="h-12 w-auto" />
          </button>

          <div className="hidden md:flex items-center space-x-8">
            {navItems.map(item => (
              <button
                key={item.key}
                onClick={() => onNavigate(item.page)}
                className={`text-sm transition-colors duration-200 flex items-center space-x-1 ${
                  currentPage === item.page 
                    ? 'text-teal-400' 
                    : 'text-slate-300 hover:text-white'
                }`}
                data-testid={`nav-${item.key}`}
              >
                {item.icon && <item.icon className="w-4 h-4" />}
                <span>{item.label}</span>
              </button>
            ))}
          </div>

          <div className="flex items-center space-x-4">
            <div className="relative">
              <button
                onClick={() => { setIsLangOpen(!isLangOpen); setIsCurrOpen(false); }}
                className="flex items-center space-x-1 text-slate-300 hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-white/5"
                data-testid="language-selector"
              >
                <span>{currentLang.flag}</span>
                <ChevronDown className="w-4 h-4" />
              </button>
              
              <AnimatePresence>
                {isLangOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-40 bg-slate-900 border border-white/10 rounded-xl shadow-xl overflow-hidden"
                  >
                    {SUPPORTED_LANGUAGES.map(lang => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          i18n.changeLanguage(lang.code);
                          setIsLangOpen(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 transition-colors ${
                          lang.code === i18n.language 
                            ? 'bg-teal-500/20 text-teal-400' 
                            : 'text-slate-300 hover:bg-white/5'
                        }`}
                        data-testid={`lang-${lang.code}`}
                      >
                        <span>{lang.flag}</span>
                        <span>{lang.name}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Currency Selector */}
            <div className="relative">
              <button
                onClick={() => { setIsCurrOpen(!isCurrOpen); setIsLangOpen(false); }}
                className="flex items-center space-x-1 text-slate-300 hover:text-white transition-colors px-2 py-1 rounded-lg hover:bg-white/5 text-sm font-mono"
                data-testid="currency-selector"
              >
                <span>{currencyConfig.symbol}</span>
                <span className="hidden sm:inline text-xs">{currency}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              
              <AnimatePresence>
                {isCurrOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 mt-2 w-36 bg-slate-900 border border-white/10 rounded-xl shadow-xl overflow-hidden"
                  >
                    {Object.entries(allCurrencies).map(([code, cfg]) => (
                      <button
                        key={code}
                        onClick={() => {
                          setCurrency(code);
                          setIsCurrOpen(false);
                        }}
                        className={`w-full px-4 py-2 text-left text-sm flex items-center space-x-2 transition-colors ${
                          code === currency 
                            ? 'bg-teal-500/20 text-teal-400' 
                            : 'text-slate-300 hover:bg-white/5'
                        }`}
                        data-testid={`currency-${code}`}
                      >
                        <span>{cfg.flag}</span>
                        <span className="font-mono">{cfg.symbol}</span>
                        <span>{code}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Translator Button */}
            <button
              onClick={() => onNavigate('translator')}
              className={`p-2 transition-colors rounded-lg ${
                currentPage === 'translator'
                  ? 'text-teal-400'
                  : 'text-slate-300 hover:text-white'
              }`}
              data-testid="nav-translator-btn"
              title="Translator"
            >
              <Languages className="w-5 h-5" />
            </button>

            {/* Trip Planner Button */}
            <button
              onClick={() => onNavigate('trip-planner')}
              className={`p-2 transition-colors rounded-lg ${
                currentPage === 'trip-planner'
                  ? 'text-teal-400'
                  : 'text-slate-300 hover:text-white'
              }`}
              data-testid="nav-trip-planner-btn"
              title="Trip Planner"
            >
              <Route className="w-5 h-5" />
            </button>

            {/* Account Button */}
            <button
              onClick={() => onNavigate(user ? 'account' : 'login')}
              className={`p-2 transition-colors rounded-lg ${
                currentPage === 'account' || currentPage === 'login'
                  ? 'text-teal-400'
                  : 'text-slate-300 hover:text-white'
              }`}
              data-testid="account-button"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-7 h-7 rounded-full border border-teal-500/30" />
              ) : (
                <UserCircle className="w-6 h-6" />
              )}
            </button>

            <button
              onClick={onCartClick}
              className="relative p-2 text-slate-300 hover:text-white transition-colors"
              data-testid="cart-button"
            >
              <ShoppingCart className="w-6 h-6" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-teal-500 text-black text-xs font-bold rounded-full flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-2 text-slate-300 hover:text-white"
              data-testid="mobile-menu-button"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden bg-slate-900 border-t border-white/10"
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map(item => (
                <button
                  key={item.key}
                  onClick={() => {
                    onNavigate(item.page);
                    setIsMenuOpen(false);
                  }}
                  className="block w-full text-left px-4 py-2 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => {
                  onNavigate('translator');
                  setIsMenuOpen(false);
                }}
                className="flex w-full items-center gap-2 text-left px-4 py-2 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                data-testid="mobile-nav-translator"
              >
                <Languages className="w-4 h-4" />
                Traduttore
              </button>
              <button
                onClick={() => {
                  onNavigate('trip-planner');
                  setIsMenuOpen(false);
                }}
                className="flex w-full items-center gap-2 text-left px-4 py-2 text-slate-300 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
                data-testid="mobile-nav-trip-planner"
              >
                <Route className="w-4 h-4" />
                Trip Planner
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export function Hero({ onSearch, onNavigate, products, onCountrySelect, onAddToCart }) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onSearch(searchQuery);
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
  };

  const handleClosePopup = () => {
    setSelectedCountry(null);
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16">
      <div className="absolute inset-0 bg-[#0f1419]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1419]/80 via-transparent to-[#0f1419] z-10 pointer-events-none" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-full h-full max-w-4xl max-h-[800px]">
            <InteractiveGlobe 
              onCountrySelect={handleCountrySelect}
              searchQuery={searchQuery}
              products={products}
            />
          </div>
        </div>
      </div>

      <div className="relative z-20 max-w-4xl mx-auto px-4 text-center">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 font-outfit tracking-tight"
        >
          {t('hero.title')}
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-lg sm:text-xl text-slate-400 mb-10 max-w-2xl mx-auto"
        >
          {t('hero.subtitle')}
        </motion.p>

        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          onSubmit={handleSearch}
          className="relative max-w-xl mx-auto mb-8"
        >
          <div className="relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-full p-2 shadow-[0_0_30px_rgba(6,182,212,0.15)]">
            <div className="flex items-center">
              <Search className="w-5 h-5 text-slate-400 ml-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('hero.searchPlaceholder')}
                className="flex-1 bg-transparent text-white placeholder-slate-500 px-4 py-3 outline-none text-lg"
                data-testid="hero-search-input"
              />
              <button
                type="submit"
                className="bg-teal-500 hover:bg-teal-400 text-black font-semibold px-6 py-3 rounded-full transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.5)]"
                data-testid="hero-search-button"
              >
                <Search className="w-5 h-5" />
              </button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            {t('hero.searchHint')}
          </p>
        </motion.form>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-6"
        >
          <button
            onClick={() => onNavigate('shop')}
            className="inline-flex items-center space-x-2 text-teal-400 hover:text-teal-300 transition-colors"
            data-testid="browse-all-button"
          >
            <span>{t('hero.cta')}</span>
            <ChevronRight className="w-5 h-5" />
          </button>
          
          <button
            onClick={() => onNavigate('map')}
            className="inline-flex items-center space-x-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full transition-colors"
            data-testid="view-map-button"
          >
            <Map className="w-4 h-4" />
            <span>{t('hero.exploreMap')}</span>
          </button>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0f1419] to-transparent z-10 pointer-events-none" />

      {/* Country popup */}
      <AnimatePresence>
        {selectedCountry && (
          <CountryPlansPopup
            country={selectedCountry}
            products={products}
            onClose={handleClosePopup}
            onAddToCart={(product) => {
              onAddToCart(product);
              handleClosePopup();
            }}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

export function Features() {
  const { t } = useTranslation();

  const features = [
    { icon: Zap, key: 'instant', color: 'from-teal-400 to-teal-600' },
    { icon: Globe, key: 'global', color: 'from-indigo-400 to-indigo-600' },
    { icon: Shield, key: 'savings', color: 'from-emerald-400 to-emerald-600' },
    { icon: Headphones, key: 'support', color: 'from-amber-400 to-amber-600' }
  ];

  return (
    <section className="py-24 bg-[#0f1419]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl sm:text-4xl font-bold text-white text-center mb-16 font-outfit"
        >
          {t('features.title')}
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <motion.div
              key={feature.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(6,182,212,0.1)]"
              data-testid={`feature-${feature.key}`}
            >
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4`}>
                <feature.icon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-white mb-2 font-outfit">
                {t(`features.${feature.key}.title`)}
              </h3>
              <p className="text-slate-400 text-sm">
                {t(`features.${feature.key}.desc`)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ProductCard({ product, onAddToCart, onToggleCompare, isInCompare, onProductClick }) {
  const { user } = useAuth();
  const { toggleWishlist, isInWishlist } = useWishlist();
  const isFav = isInWishlist(product.id);
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();

  // Use landmark image if available, otherwise flag
  const GLOBAL_IMG = 'https://static.prod-images.emergentagent.com/jobs/cf702e61-1c5c-4082-ad24-9b20f1215a59/images/668e1ce74661caafabcac565026821718a54845e67968b685872eb55768dd515.png';
  const isGlobal = product.country_code === 'GLOBAL';
  const landmarkUrl = product.country_code && !isGlobal ? getLandmarkUrl(product.country_code) : null;
  const bgImageUrl = isGlobal ? GLOBAL_IMG : (landmarkUrl || product.flag_url || (product.country_code ? `https://flagcdn.com/w640/${product.country_code.toLowerCase()}.png` : ''));
  const flagSmall = isGlobal ? GLOBAL_IMG : (product.country_code ? `https://flagcdn.com/w80/${product.country_code.toLowerCase()}.png` : '');
  const hasLandmark = !!landmarkUrl;

  const getDataInfo = () => {
    const shortDesc = product.short_description || '';
    const name = product.name || '';
    const match = shortDesc.match(/(\d+)\s*(GB|gb)/i) || name.match(/(\d+)\s*(GB|gb)/i);
    if (match) return `${match[1]}GB`;
    const meta = product.meta_data?.find(m => m.key === 'data');
    return meta?.value || '';
  };

  const plansCount = product.variations_count || '';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ scale: 1.02 }}
      className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl overflow-hidden hover:border-teal-500/50 transition-all duration-300 hover:shadow-[0_0_30px_rgba(6,182,212,0.15)] cursor-pointer"
      data-testid={`product-card-${product.id}`}
      onClick={() => onProductClick?.(product)}
    >
      <div className="relative h-40 overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900">
        {bgImageUrl && (
          <img
            src={bgImageUrl}
            alt={product.country_name || product.name}
            className={`w-full h-full object-cover ${hasLandmark ? 'opacity-70 scale-105' : 'opacity-40 blur-[2px] scale-110'}`}
          />
        )}
        <div className={`absolute inset-0 ${hasLandmark ? 'bg-gradient-to-t from-[#0f1419] via-[#0f1419]/50 to-transparent' : 'bg-gradient-to-t from-[#0f1419] via-[#0f1419]/60 to-transparent'}`} />

        {/* Flag badge and country name overlay */}
        <div className="absolute bottom-3 left-4 flex items-center gap-2.5">
          {flagSmall && (
            <img
              src={flagSmall}
              alt=""
              className="w-8 h-6 rounded shadow-lg object-cover border border-white/20"
            />
          )}
          <h3 className="text-lg font-bold text-white font-outfit drop-shadow-lg line-clamp-1">
            {product.country_name || product.name?.replace('eSIM for ', '')}
          </h3>
        </div>

        {product.on_sale && (
          <div className="absolute top-3 right-3 bg-emerald-500 text-black text-xs font-bold px-2 py-1 rounded-full z-10">
            SALE
          </div>
        )}
        {onToggleCompare && (
          <div className="absolute top-3 left-3 z-10 flex space-x-2">
            <CompareButton
              product={product}
              isSelected={isInCompare}
              onToggle={onToggleCompare}
            />
            {user && (
              <button
                onClick={(e) => { e.stopPropagation(); toggleWishlist(product); }}
                className={`p-2 rounded-full transition-all duration-200 shadow-lg ${
                  isFav
                    ? 'bg-rose-500 text-white'
                    : 'bg-slate-900/90 text-slate-400 border border-white/20 hover:text-rose-400 hover:border-rose-400/40'
                }`}
                data-testid={`wishlist-toggle-${product.id}`}
              >
                <Heart className={`w-4 h-4 ${isFav ? 'fill-current' : ''}`} />
              </button>
            )}
          </div>
        )}
      </div>

      <div className="p-5">
        <div className="flex items-center gap-3 mb-3 text-xs text-slate-500">
          {product.categories?.[0] && (
            <span className="bg-teal-500/10 text-teal-400 px-2 py-0.5 rounded-full">
              {product.categories[0].name}
            </span>
          )}
          {plansCount && (
            <span>{plansCount} {t('products.plans', 'plans')}</span>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-baseline space-x-2">
            <span className="text-xs text-slate-500 mr-0.5">{t('products.from')}</span>
            <span className="text-2xl font-bold text-white font-mono">
              {formatPrice(product.price)}
            </span>
          </div>

          <button
            onClick={(e) => { e.stopPropagation(); onProductClick?.(product); }}
            className="bg-teal-500 hover:bg-teal-400 text-black font-semibold px-4 py-2 rounded-full text-sm transition-all duration-200 hover:shadow-[0_0_15px_rgba(6,182,212,0.5)]"
            data-testid={`view-plans-${product.id}`}
          >
            {t('products.viewPlans', 'View Plans')}
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export function ProductGrid({ onAddToCart, searchQuery, onToggleCompare, isInCompare, onProductClick }) {
  const { t, i18n } = useTranslation();
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { key: 'all', slug: null, label: t('products.filters.all') },
    { key: 'europe', slug: 'europe', label: t('products.filters.europe', 'Europe') },
    { key: 'asia', slug: 'asia', label: t('products.filters.asia', 'Asia') },
    { key: 'north-america', slug: 'north-america', label: t('products.filters.northAmerica', 'N. America') },
    { key: 'south-america', slug: 'south-america', label: t('products.filters.southAmerica', 'S. America') },
    { key: 'middle-east', slug: 'middle-east', label: t('products.filters.middleEast', 'Middle East') },
    { key: 'africa', slug: 'africa', label: t('products.filters.africa', 'Africa') },
    { key: 'caribbean', slug: 'caribbean', label: t('products.filters.caribbean', 'Caribbean') },
    { key: 'oceania', slug: 'oceania', label: t('products.filters.oceania', 'Oceania') },
    { key: 'global', slug: 'global', label: 'Global' },
  ];

  // Load all products once (no search param - we filter client-side for multilingual)
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        params.append('per_page', '500');
        const response = await axios.get(`${API}/products?${params.toString()}`);
        setAllProducts(response.data.products || []);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Filter products: multilingual search + category filter
  const products = useMemo(() => {
    let filtered = allProducts;

    // Multilingual search
    if (searchQuery && searchQuery.trim()) {
      filtered = searchProducts(filtered, searchQuery, i18n.language);
    }

    // Category filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(p => p.categories?.some(c => c.slug === filters.find(f => f.key === activeFilter)?.slug));
    }

    return filtered;
  }, [allProducts, searchQuery, activeFilter, i18n.language]);

  return (
    <section className="py-24 bg-[#0f1419]" id="products">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-12">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-2">
              {t('products.title')}
            </h2>
            {onToggleCompare && (
              <div className="flex items-center space-x-2 mt-2 bg-teal-500/10 border border-teal-500/20 rounded-lg px-3 py-2">
                <Scale className="w-4 h-4 text-teal-400 flex-shrink-0" />
                <span className="text-teal-300 text-sm font-medium">{t('products.compareHint')}</span>
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-4 sm:mt-0">
            {filters.map(filter => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  activeFilter === filter.key
                    ? 'bg-teal-500 text-black'
                    : 'bg-white/5 text-slate-300 hover:bg-white/10'
                }`}
                data-testid={`filter-${filter.key}`}
              >
                {t(`products.filters.${filter.key}`, filter.label)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white/5 rounded-2xl h-72 animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                onAddToCart={onAddToCart}
                onToggleCompare={onToggleCompare}
                isInCompare={isInCompare ? isInCompare(product.id) : false}
                onProductClick={onProductClick}
              />
            ))}
          </div>
        )}

        {!loading && products.length === 0 && (
          <div className="text-center py-12">
            <p className="text-slate-400 text-lg">{t('common.error')}</p>
          </div>
        )}
      </div>
    </section>
  );
}

const FEATURED_IDS = [100097, 100001, 100200, 100066, 100181, 100071, 100099, 100195];

export function FeaturedDestinations({ products, onProductClick, onAddToCart, onToggleCompare, isInCompare, onViewAll }) {
  const { t } = useTranslation();
  const featured = FEATURED_IDS
    .map(id => products.find(p => p.id === id))
    .filter(Boolean);

  if (featured.length === 0) return null;

  return (
    <section className="py-20 bg-[#0f1419]" id="featured" data-testid="featured-destinations">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-3"
          >
            {t('products.topDestinations')}
          </motion.h2>
          <p className="text-slate-400 text-base max-w-xl mx-auto">
            {t('products.topDestinationsDesc')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {featured.map(product => (
            <ProductCard
              key={product.id}
              product={product}
              onAddToCart={onAddToCart}
              onToggleCompare={onToggleCompare}
              isInCompare={isInCompare ? isInCompare(product.id) : false}
              onProductClick={onProductClick}
            />
          ))}
        </div>

        <div className="text-center mt-10">
          <button
            onClick={onViewAll}
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-teal-500/50 text-white font-semibold px-8 py-3.5 rounded-full transition-all duration-300 hover:shadow-[0_0_20px_rgba(6,182,212,0.2)]"
            data-testid="view-all-destinations"
          >
            <Globe className="w-5 h-5 text-teal-400" />
            <span>{t('products.discoverAll')}</span>
            <ChevronRight className="w-4 h-4 text-teal-400" />
          </button>
        </div>
      </div>
    </section>
  );
}


export function HowItWorks() {
  const { t } = useTranslation();

  const steps = [
    { number: '01', key: 'step1', icon: '🎯' },
    { number: '02', key: 'step2', icon: '📧' },
    { number: '03', key: 'step3', icon: '📱' }
  ];

  return (
    <section className="py-24 bg-slate-900/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-3xl sm:text-4xl font-bold text-white text-center mb-16 font-outfit"
        >
          {t('howItWorks.title')}
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={step.key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="relative text-center"
              data-testid={`how-it-works-${step.key}`}
            >
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 left-1/2 w-full h-0.5 bg-gradient-to-r from-teal-500/50 to-transparent" />
              )}
              
              <div className="relative inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-teal-500/20 to-indigo-600/20 border border-teal-500/30 mb-6">
                <span className="text-4xl">{step.icon}</span>
                <span className="absolute -top-2 -right-2 w-8 h-8 bg-teal-500 text-black text-sm font-bold rounded-full flex items-center justify-center font-mono">
                  {step.number}
                </span>
              </div>

              <h3 className="text-xl font-semibold text-white mb-3 font-outfit">
                {t(`howItWorks.${step.key}.title`)}
              </h3>
              <p className="text-slate-400">
                {t(`howItWorks.${step.key}.desc`)}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CartDrawer({ isOpen, onClose, cart, onRemove, onUpdateQuantity, cartTotal, onCheckout }) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />
          
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-slate-900 border-l border-white/10 z-50 flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <h2 className="text-xl font-bold text-white font-outfit">{t('cart.title')}</h2>
              <button
                onClick={onClose}
                className="p-2 text-slate-400 hover:text-white transition-colors"
                data-testid="close-cart"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {cart.items.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingCart className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">{t('cart.empty')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {cart.items.map(item => (
                    <motion.div
                      key={item.product_id}
                      layout
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -100 }}
                      className="bg-white/5 rounded-xl p-4 flex items-center space-x-4"
                      data-testid={`cart-item-${item.product_id}`}
                    >
                      {item.image && (
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-medium truncate">{item.name}</h4>
                        <p className="text-teal-400 font-mono">{formatPrice(item.price)}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => onUpdateQuantity(item.product_id, Math.max(0, item.quantity - 1))}
                          className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
                        >
                          -
                        </button>
                        <span className="text-white font-mono w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => onUpdateQuantity(item.product_id, item.quantity + 1)}
                          className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors"
                        >
                          +
                        </button>
                      </div>
                      <button
                        onClick={() => onRemove(item.product_id)}
                        className="p-2 text-slate-400 hover:text-rose-500 transition-colors"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>

            {cart.items.length > 0 && (
              <div className="p-6 border-t border-white/10">
                <div className="flex items-center justify-between mb-6">
                  <span className="text-slate-400">{t('cart.total')}</span>
                  <span className="text-2xl font-bold text-white font-mono">{formatPrice(cartTotal)}</span>
                </div>
                <button
                  onClick={onCheckout}
                  className="w-full bg-teal-500 hover:bg-teal-400 text-black font-bold py-4 rounded-full transition-all duration-200 hover:shadow-[0_0_20px_rgba(6,182,212,0.5)]"
                  data-testid="checkout-button"
                >
                  {t('cart.checkout')}
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export function Footer({ onNavigate }) {
  const { t } = useTranslation();

  return (
    <footer className="bg-[#0f1419] border-t border-white/10 py-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center mb-4">
              <img src="/telcavoip-logo-white.png" alt="TelcaVoIP eSIM" className="h-14 w-auto" />
            </div>
            <p className="text-slate-400 text-sm mb-4">{t('footer.tagline')}</p>
            <a
              href="https://telcavoip.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-1 text-xs text-slate-500 hover:text-teal-400 transition-colors"
              data-testid="telcavoip-link"
            >
              <span>{t('footer.serviceBy')}</span>
              <span className="text-teal-400 font-semibold">TelcaVoIP.AI</span>
            </a>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.quickLinks')}</h4>
            <ul className="space-y-2">
              {['home', 'shop', 'howItWorks', 'faq'].map(key => (
                <li key={key}>
                  <button
                    onClick={() => onNavigate(key === 'howItWorks' ? 'how-it-works' : key)}
                    className="text-slate-400 hover:text-teal-400 text-sm transition-colors"
                  >
                    {t(`nav.${key}`)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Our Locations */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.locations')}</h4>
            <div className="space-y-4">
              <div>
                <p className="text-teal-400 text-xs font-medium mb-1">{t('footer.euHQ')}</p>
                <div className="flex items-start space-x-2">
                  <MapPin className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-slate-400 text-sm">Szczecin, POLAND (EU)</p>
                    <p className="text-slate-500 text-xs">VAT PL8513242072</p>
                  </div>
                </div>
              </div>
              <div>
                <p className="text-teal-400 text-xs font-medium mb-1">{t('footer.usHQ')}</p>
                <div className="flex items-start space-x-2">
                  <MapPin className="w-3.5 h-3.5 text-slate-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-slate-400 text-sm">Dover, Delaware (USA)</p>
                    <p className="text-slate-500 text-xs">EIN 36-5131426</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Support & Legal */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t('footer.support')}</h4>
            <div className="space-y-3 mb-6">
              <a href="#" onClick={openWhatsApp} className="flex items-center space-x-2 text-slate-400 hover:text-green-400 text-sm transition-colors" data-testid="support-whatsapp">
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                <span>{getWhatsAppNumber()}</span>
              </a>
              <a href="#" onClick={openMailTo} className="flex items-center space-x-2 text-slate-400 hover:text-teal-400 text-sm transition-colors">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <span>{getSupportEmail()}</span>
              </a>
            </div>
            <h4 className="text-white font-semibold mb-3">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              <li><button onClick={() => onNavigate('privacy')} className="text-slate-400 text-sm hover:text-teal-400 transition-colors">{t('footer.privacy')}</button></li>
              <li><button onClick={() => onNavigate('terms')} className="text-slate-400 text-sm hover:text-teal-400 transition-colors">{t('footer.terms')}</button></li>
              <li><button onClick={() => onNavigate('refund')} className="text-slate-400 text-sm hover:text-teal-400 transition-colors">{t('footer.refund')}</button></li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10">
          {/* Trust Badges */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 mb-6" data-testid="trust-badges">
            <div className="flex items-center gap-1.5 text-slate-400">
              <CreditCard className="w-4 h-4 text-teal-400" />
              <span className="text-xs">Stripe</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-400">
              <svg className="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="currentColor"><path d="M7.076 21.337H2.47a.641.641 0 01-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 00-.607-.541c1.678 4.502-1.088 7.194-5.637 7.194h-2.19a1.08 1.08 0 00-1.05.9l-1.12 7.106-.322 2.04a.641.641 0 00.633.74h4.456c.46 0 .85-.334.922-.788l.038-.2.73-4.627.047-.256a.93.93 0 01.922-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471a3.71 3.71 0 00-1.065-.888"/></svg>
              <span className="text-xs">PayPal</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-400">
              <Lock className="w-4 h-4 text-emerald-400" />
              <span className="text-xs">SSL</span>
            </div>
            <div className="flex items-center gap-1.5 text-slate-400">
              <Shield className="w-4 h-4 text-blue-400" />
              <span className="text-xs">GDPR</span>
            </div>
          </div>

          {/* Copyright */}
          <p className="text-slate-500 text-sm text-center mb-4" data-testid="copyright">
            eSIM4You &mdash; Copyright &copy; 2025 TelcaVoIP eSIM | TelcaVoIP International EU | VAT PL8513242072
          </p>

          {/* TelcaVoIP.AI Badge */}
          <div className="flex justify-center">
            <a
              href="https://telcavoip.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white/5 border border-teal-500/30 hover:border-teal-500/60 transition-colors"
              data-testid="telcavoip-badge"
            >
              <span className="text-slate-400 text-xs">eSIM4You made with</span>
              <span className="text-rose-500 text-sm">&hearts;</span>
              <span className="text-teal-400 text-xs font-semibold">TelcaVoIP.AI</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
